import { serverTimestamp, type Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, observable, toJS, when } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { PlaniraLogType } from "../../../shared/types";
import {
  type ModuleCompleteInfo,
  ModuleDoneType,
  ModuleState,
  ModuleType,
  PlanQuestionType,
} from "../../../templates/types";
import ActivityModel, {
  type ActivityModelData,
} from "../../activities/ActivityModel";
import {
  ACTIVITY_PRIORITY_TYPES,
  ActivityEventType,
} from "../../activities/types";
import type { CompletedData } from "../../business/modules/BusinessModuleModel";
import type { Api } from "../../core/apis";
import { BaseModel, type BaseModelData } from "../../core/BaseModel";
import { scrollToTop } from "../../core/screens/types";
import { showConfetti } from "../../core/states.ts/showConfetti";
import type { Stores } from "../../core/stores";
import { trackEvent } from "../../core/trackEvent";
import { assertNever } from "../../core/utils";
import type PlanModel from "../../plans/PlanModel";
import { PlanModelStep } from "../../plans/PlanModel";
import type { IconProp } from "../../ui/icons";
import { log } from "../../ui/log";
import { navigateGlobal } from "../../ui/navigation/NavigateGlobal";
import type { EditorValue } from "../../ui/YooptaEditor";
import type BaseModuleDataModel from "./BaseModuleDataModel";
import {
  type BaseModuleModelData,
  ModuleDataType,
} from "./BaseModuleDataModel";
import { sanitizeForFirestore } from "./sanitizeForFirestore";

export type MenuOption = {
  onPress: () => void;
  checked?: boolean;
  icon: IconProp;
  label: string;
};

export type Snapshot = Record<
  string,
  | string
  | number
  | (string | number)[]
  | CompletedData[]
  | Timestamp
  | { lat: number; lng: number }[]
>;

export type BaseModuleType = BaseModuleModel<
  Snapshot,
  ModuleModelData<Snapshot>
>;

export interface ModuleModelData<SnapshotType = object> extends BaseModelData {
  type: ModuleType;
  doneType: ModuleDoneType;
  questionType: PlanQuestionType;
  completeAction: ModuleCompleteInfo | null;

  state: ModuleState;

  planUid: string;
  sectionUid: string;

  title: string | null;
  emoji: string | null;
  descriptionYooptaEditor: EditorValue | null;

  lastActivity: Timestamp;
  comments: number;
  snapshot: SnapshotType;

  recentActivity: ActivityModelData[];
  isMuted: boolean;

  initiated: boolean;
}

export const moduleTypeToReadable: Record<ModuleType, string> = {
  [ModuleType.PlanInfo]: "Plan info",
  [ModuleType.Info]: "Info",
  [ModuleType.Checkboxes]: "To-do",
  [ModuleType.Calendar]: "Calendar",
  [ModuleType.Poll]: "Poll",
  [ModuleType.Location]: "Location",
  [ModuleType.DateDue]: "Date Due",
  [ModuleType.Reminder]: "Reminder",
  [ModuleType.Expense]: "Expenses",
  [ModuleType.Tracker]: "Tracker",
  [ModuleType.Rsvp]: "RSVP",
};

// Temp function to keep old modules in sync with correct data
export const getDefaultDoneType = (m: ModuleType) => {
  const hasDoneBox = [
    ModuleType.Calendar,
    ModuleType.Location,
    ModuleType.Poll,
    ModuleType.DateDue,
    ModuleType.Reminder,
    ModuleType.Rsvp,
  ].includes(m);
  return hasDoneBox ? ModuleDoneType.Voting : ModuleDoneType.None;
};

export default abstract class BaseModuleModel<
  SnapshotType extends Snapshot,
  T extends ModuleModelData<SnapshotType>,
> extends BaseModel<T> {
  constructor(stores: Stores, api: Api, data: T) {
    super(stores, api, data);
    Object.assign(this.data, {
      title: data.title ?? null,
      lastActivity: data.lastActivity ?? {
        nanoseconds: 0,
        seconds: Math.round(Date.now() / 1000),
      },
      comments: data.comments ?? 0,
      snapshot: data.snapshot ?? {},
      recentActivity: data.recentActivity ?? [],
      descriptionYooptaEditor: data.descriptionYooptaEditor ?? null,
      isMuted: data.isMuted ?? false,
      doneType: data.doneType ?? getDefaultDoneType(data.type),
      questionType: data.questionType ?? PlanQuestionType.None,
      completeAction: data.completeAction ?? null,
    });

    makeObservable(this);
  }

  @observable activitySheetOpen = true;

  // Used to show setting
  hasHelperGuide = false;

  @observable doneFlowEnabled = false;

  @action
  disableDoneFlow() {
    this.doneFlowEnabled = false;
  }

  @action
  onDone() {
    this.doneFlowEnabled = true;
  }

  abstract updateSnapshotWithoutSave(): void;

  @action setActivitySheetOpen(open: boolean) {
    this.activitySheetOpen = open;
  }

  @action save() {
    return this.api.modulesApi.set(this);
  }

  @action async setAsArchived(status = true) {
    this.data.archived = status;
    return this.api.modulesApi.update(this.uid, {
      archived: this.data.archived,
    });
  }

  sendTelemetryEvent(event: string) {
    void trackEvent(`${this.data.type}_${event}`);
  }

  @computed get hasVoting(): boolean {
    return (
      this.data.doneType === ModuleDoneType.Voting ||
      getDefaultDoneType(this.data.type) === ModuleDoneType.Voting
    );
  }

  @computed get isEditable() {
    return !this.isDone;
  }

  @computed get isOnboarding() {
    return this.data.doneType === ModuleDoneType.Onboarding;
  }

  @computed get isStatic() {
    return this.data.doneType === ModuleDoneType.Static;
  }

  @computed get hasDescription() {
    return !!(
      this.data.descriptionYooptaEditor &&
      Object.keys(this.data.descriptionYooptaEditor).length
    );
  }

  @action init() {
    this.data.initiated = true;
    return this.save();
  }

  @action async resetCompletionType() {
    this.data.completeAction = null;
    await this.api.modulesApi.update(this.uid, {
      completeAction: this.data.completeAction,
    });
  }

  @action setDoneType = async (doneType: ModuleDoneType) => {
    if (this.data.doneType !== doneType) {
      this.data.doneType = doneType;
      await this.api.modulesApi.update(this.uid, {
        doneType: this.data.doneType,
      });

      this.sendTelemetryEvent(`done_type_set_${this.data.doneType}`);
    }
  };

  @action setTitle = async (title: string, skipSave?: boolean) => {
    const oldTitle = this.data.title;

    if (oldTitle !== title) {
      this.data.title = title;
      if (!skipSave) {
        await this.api.modulesApi.update(this.uid, { title: this.data.title });

        this.postActivityEvent(
          ActivityEventType.Edited,
          TKey.ActivityTitleChange,
          { oldValue: oldTitle ?? "", newValue: this.data.title },
        );
        this.sendTelemetryEvent("title_updated");
      }
    }
  };

  @action setDescriptionEditor = async (data: EditorValue | null) => {
    if (data !== this.data.descriptionYooptaEditor) {
      const sanitizedData = {
        data: sanitizeForFirestore(data?.data),
        markdown: data?.markdown,
      };

      this.data.descriptionYooptaEditor = sanitizedData;
      this.updateTimestampWithoutSave();
      await this.save();

      this.postActivityEvent(
        ActivityEventType.Edited,
        TKey.ActivityDescriptionChanged,
        {},
      );
      this.sendTelemetryEvent("text_updated");

      this.plan?.trackProgress(PlanModelStep.hasDescription, true);
    }
  };

  @action setEmoji = async (emoji: string, skipSave?: boolean) => {
    const oldEmoji = this.data.emoji;

    if (oldEmoji !== emoji) {
      this.data.emoji = emoji;
      if (!skipSave) {
        await this.api.modulesApi.update(this.uid, { emoji: this.data.emoji });

        this.postActivityEvent(
          ActivityEventType.Edited,
          TKey.ActivityEmojiChanged,
          { oldValue: oldEmoji ?? "", newValue: this.data.emoji },
        );
        this.sendTelemetryEvent("emoji_updated");
      }
    }
  };

  @action setMuted = async (isMuted: boolean) => {
    if (this.data.isMuted !== isMuted) {
      this.data.isMuted = isMuted;
      await this.api.modulesApi.update(this.uid, {
        isMuted: this.data.isMuted,
      });
    }
  };

  @action async toggleDoneState() {
    if (this.data.state === ModuleState.Done) {
      this.data.state = ModuleState.Planning;
    } else if (this.data.state === ModuleState.Planning) {
      this.data.state = ModuleState.Done;

      // If done section found, move it automatically.
      showConfetti();

      const plan = this.plan as PlanModel;

      plan.trackProgress(PlanModelStep.hasSetCardToDone, true);

      void plan.addPlaniraLogItem(
        PlaniraLogType.CardIsDone,
        `${this.data.title} - type: ${this.data.type} - done: ${this.data.doneType}`,
      );

      if (plan.isQuickstart) {
        // Go to the overview page if quickstart
        navigateGlobal("Plan", {
          planUid: this.plan.uid,
        });
      }

      scrollToTop();
    } else {
      log.dev("Invalid state found when toggling!", this.data.state);
      return;
    }

    const isDone = this.data.state === ModuleState.Done;

    this.updateTimestampWithoutSave();
    this.api.modulesApi.set(this);
    this.sendTelemetryEvent(isDone ? "set_as_done" : "reset_done");

    await this.postActivityEvent(
      ActivityEventType.StateChange,
      isDone ? TKey.ActivityStateSetToDone : TKey.ActivityStateSetToPlanning,
      {},
    );
  }

  @action async addNewData<T>(
    type: ModuleDataType,
    data: Partial<T>,
    textSID?: TKey,
    textData?: Record<string, string | number>,
  ) {
    const dataItem = await this.api.modulesDataApi.add({
      ts: serverTimestamp() as Timestamp,
      planUid: this.plan.uid,
      moduleUid: this.uid,
      type,
      descriptionYooptaEditor: null,
      index: this.nextDataIndex,
      timestamp: serverTimestamp() as Timestamp,
      ...data,
    });

    this.updateSnapshotWithoutSave();
    this.api.modulesApi.update(this.uid, {
      snapshot: this.data.snapshot,
    });

    if (textSID && textData) {
      this.postActivityEvent(
        ActivityEventType.DataAdded,
        textSID,
        textData,
        dataItem.uid,
      );
    }

    return dataItem;
  }

  @action async postActivityEvent(
    type: ActivityEventType,
    textSID: TKey,
    textData: Record<string, string | number>,
    dataUid: string | null = null,
  ) {
    const plan = this.plan;

    const actingAsUser = plan.userInPlan;

    const activityEvent = await this.api.activitiesApi.add({
      ts: serverTimestamp() as Timestamp,
      reactions: [],
      planUid: plan.uid,
      moduleUid: this.uid,
      dataUid: dataUid,
      userUid: actingAsUser?.uid ?? "",
      timestamp: serverTimestamp() as Timestamp,
      type,
      textSID,
      textData,
    });

    if (ACTIVITY_PRIORITY_TYPES.includes(type)) {
      this.addRecentActivityEventToModule(activityEvent);
    }

    if (type === ActivityEventType.Comment) {
      this.data.comments++;

      plan.trackProgress(PlanModelStep.hasComments, true);

      plan.addPlaniraLogItem(
        PlaniraLogType.CardComment,
        `Comment "${activityEvent.data.textData.comment}" was created`,
      );
    }

    if (!this.data.isMuted) {
      this.updateTimestampWithoutSave();
    }

    await this.api.modulesApi.set(this);

    trackEvent(`activity_type_${type}`);
    this.sendTelemetryEvent(`activity_${type}`);

    if (!this.data.isMuted) {
      void this.maybeAddEmailData(activityEvent, textSID, textData);
    }
  }

  private async maybeAddEmailData(
    activityEvent: ActivityModel,
    textSID: TKey,
    textData: Record<string, string | number>,
  ) {
    const plan = this.plan;

    if (plan.hasRegistredUsers) {
      const user = this.plan.userInPlan;
      if (user) {
        // Add an entry to be sent out later on
        this.api.emailDataApi.add({
          createdAt: serverTimestamp() as Timestamp,
          refUid: activityEvent.uid,
          planira: {
            uid: plan.uid,
            name: plan.data.title,
          },
          module: {
            uid: this.uid,
            typeEnum: this.data.type,
            name: this.data.title ?? "",
          },
          author: {
            uid: user.uid,
            name: user.data.displayName,
          },
          typeEnum: activityEvent.data.type,
          textSID,
          textData,
        });
        trackEvent("activity_email_data_added");
      }
    }
  }

  @action addRecentActivityEventToModule(event: ActivityModel) {
    if (this.data.recentActivity.length === 5) {
      this.data.recentActivity.pop();
    }
    this.data.recentActivity.unshift({
      uid: event.uid,
      ...event.toFireStore(),
    });
  }

  @action async deleteRecentActivityEventToModule(event: ActivityModel) {
    this.data.recentActivity = this.data.recentActivity.filter(
      (r) => r.uid !== event.uid,
    );
    void this.api.modulesApi.update(this.uid, {
      recentActivity: this.data.recentActivity,
    });
  }

  @action async deleteModule() {
    const section = this.section;
    section.data.moduleIds = section.data.moduleIds.filter(
      (mUid) => mUid !== this.uid,
    );
    await this.api.sectionsApi.set(section);

    // TODO - move over to cloud-function?
    // await this.api.activitiesApi.deleteForModuleUid(this.uid);

    this.api.modulesApi.delete(this);
    this.sendTelemetryEvent("deleted");
  }

  @action updateTimestampWithoutSave() {
    this.data.lastActivity = serverTimestamp() as Timestamp;

    // also update current user visit
    this.plan?.userInPlan?.updateVisitedTimestamp(this.uid);
  }

  @action updateTimestampWithSave() {
    this.updateTimestampWithoutSave();
    this.api.modulesApi.update(this.uid, {
      lastActivity: this.data.lastActivity,
    });
  }

  @computed get userUid() {
    return this.plan.userInPlan?.uid;
  }

  @computed get canMoveUp() {
    return this.section.data.moduleIds.indexOf(this.uid) > 0;
  }

  @computed get canMoveDown() {
    return this.section.data.moduleIds.slice().reverse().indexOf(this.uid) > 0;
  }

  @computed get hasNewActivity() {
    const user = this.plan?.userInPlan;

    if (!user) {
      return false;
    }

    if (!user.data.moduleVisits[this.uid]) {
      return false; // never visited
    }

    if (this.isStatic && !this.isDone) {
      return true;
    }

    return (
      this.data.lastActivity.seconds > user.data.moduleVisits[this.uid].seconds
    );
  }

  @computed get plan(): PlanModel {
    const plan = this.stores.plansStore.byId.get(this.data.planUid);
    if (!plan) {
      throw Error("No plan found!");
    }
    return plan;
  }

  @computed get section() {
    const sectionFound = this.stores.sectionsStore.byId.get(
      this.data.sectionUid,
    );
    if (!sectionFound) {
      throw Error("No section found!");
    }
    return sectionFound;
  }

  @computed get activityEvents(): ActivityModel[] {
    return this.stores.activitiesStore.all
      .filter((event) => event.data.moduleUid === this.uid)
      .sort((a, b) => {
        return (a.data.timestamp?.seconds ?? Date.now()) <
          (b.data.timestamp?.seconds ?? Date.now())
          ? 1
          : -1;
      });
  }

  @computed get recentActivityModuleEvents(): ActivityModel[] {
    return this.data.recentActivity.map(
      (data) => new ActivityModel(this.stores, this.api, data),
    );
  }

  @computed get nextDataIndex(): number {
    if (this.dataItems.length) {
      return this.dataItems[this.dataItems.length - 1].data.index + 1;
    }
    return 1;
  }

  @computed get dataItems(): BaseModuleDataModel<BaseModuleModelData>[] {
    return this.stores.modulesDataStore.all
      .filter((event) => event.data.moduleUid === this.uid)
      .sort((a, b) => {
        return a.data.index < b.data.index ? -1 : 1;
      });
  }

  @computed get isDone() {
    return this.data.state === ModuleState.Done;
  }

  toFireStore(): Omit<ModuleModelData<SnapshotType>, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }

  async copy(): Promise<BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>> {
    // Add a new module with same data

    const d = toJS(this.data);
    d.title = `[Copy] ${d.title}`;
    d.recentActivity = [];
    d.comments = 0;
    d.lastActivity = serverTimestamp() as Timestamp;

    // @ts-expect-error
    delete d.uid;

    const newModule = await this.api.modulesApi.add(d);

    // Save module to section
    this.section.data.moduleIds.push(newModule.uid);
    await this.api.sectionsApi.set(this.section);

    const dataItems = toJS(this.dataItems);

    await Promise.all(
      dataItems.map(async (d) => {
        let newData = toJS(d.data);

        // @ts-expect-error
        delete newData.uid;
        const op = d.data.type;

        // Reset item when copied
        switch (op) {
          case ModuleDataType.TrackerItem:
          case ModuleDataType.Settlement:
          case ModuleDataType.Expense:
          case ModuleDataType.Rating:
          case ModuleDataType.CalendarItem:
          case ModuleDataType.RsvpUser:
          case ModuleDataType.PollVote:
            // Skip copy
            return;

          case ModuleDataType.Checkbox:
            newData = {
              ...newData,
              // @ts-expect-error // TODO -how?
              assignedTo: [],
              checked: false,
            };
            break;
          case ModuleDataType.Location:
          case ModuleDataType.PollOption:
            // Do copy item
            break;

          default:
            assertNever(op);
        }

        // Break to get here
        return this.api.modulesDataApi.add(
          {
            ...newData,
            moduleUid: newModule.uid,
          },
          false,
        );
      }),
    );

    when(
      () => newModule.dataItems.length === dataItems.length,
      async () => {
        // Update snapshot after items are added
        newModule.updateSnapshotWithoutSave();
        await this.api.modulesApi.update(newModule.uid, {
          snapshot: newModule.data.snapshot,
        });
        console.info("Snapshot updated");
      },
    );

    console.info("Copy done");

    if (this.data.title) {
      void newModule.postActivityEvent(
        ActivityEventType.Added,
        TKey.ActivityModuleCopied,
        {
          label: this.data.title,
        },
      );
    }

    return newModule;
  }

  abstract render(): JSX.Element;

  abstract renderStatic(): JSX.Element | null;

  abstract renderBlock(props: {
    active?: boolean;
    upcoming?: boolean;
  }): JSX.Element;

  abstract renderStaticBlock(): JSX.Element;

  abstract renderIcon(size: number): JSX.Element;

  renderBlockSetup?: (() => JSX.Element) | undefined;

  renderMenuOptions(): MenuOption[] {
    return [];
  }
}
